import React from 'react';

const OrthoPrintSummary = ({ data, diagnosisData, zone, isSubmitted }) => (
  <>
    <div style={{ borderBottom: '2px solid #4f46e5', paddingBottom: 12, marginBottom: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <img src="/logo192.png" alt="AssessMonk" style={{ width: 36, height: 36, borderRadius: 8, objectFit: 'contain' }} />
          <div>
            <div style={{ fontSize: 16, fontWeight: 900, color: '#0f172a', letterSpacing: '-0.02em' }}>AssessMonk</div>
            <div style={{ fontSize: 9, fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Physiotherapy Assessment</div>
          </div>
        </div>
        <div style={{ fontSize: 10, color: '#64748b', fontWeight: 600 }}>assessmonk.com</div>
      </div>
      <h1 style={{ margin: '0 0 2px', fontSize: 15, fontWeight: 800 }}>🦴 Orthopaedic Assessment — {zone || 'Unspecified Zone'}</h1>
      <div style={{ fontSize: 11, color: '#64748b' }}>Printed: {new Date().toLocaleString()}</div>
    </div>

    <section style={{ marginBottom: 14 }}>
      <h2 style={{ fontSize: 13, fontWeight: 800, background: '#eff6ff', padding: '4px 8px', margin: '0 0 8px', borderRadius: 4 }}>👤 Patient Information</h2>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: '4px 16px' }}>
        {[['Name', data.name], ['Age', data.age], ['Gender', data.gender], ['Occupation', data.occupation],
          ['Reg No.', data.regNo], ['Date', data.date], ['Type', data.type], ['Phone', data.phone],
          ['Dominant Hand', data.dominantHand], ['Activity Level', data.activityLevel],
        ].filter(([, v]) => v).map(([l, v]) => (
          <div key={l} style={{ marginBottom: 4 }}><span style={{ fontWeight: 700, color: '#475569' }}>{l}: </span>{v}</div>
        ))}
      </div>
    </section>

    {(data.chiefComplaint || data.hpiText || data.symSeverity || data.aggravating) && (
      <section style={{ marginBottom: 14 }}>
        <h2 style={{ fontSize: 13, fontWeight: 800, background: '#f5f3ff', padding: '4px 8px', margin: '0 0 8px', borderRadius: 4 }}>📋 Subjective History</h2>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: '4px 16px' }}>
          {[['Chief Complaint', data.chiefComplaint], ['HPI', data.hpiText], ['Medical History', data.historyMedical], ['Personal History', data.historyPersonal],
            ['Family History', data.historyFamily], ['Side', data.symSide], ['Onset', data.symOnset],
            ['Duration (weeks)', data.symDurationWeeks], ['Pain Severity (VAS)', data.symSeverity ? `${data.symSeverity}/10` : ''],
            ['Nature of Pain', data.symNature], ['Location', data.symLocation], ['Radiation', data.symRadiation],
            ['Mechanism of Injury', data.mechanismOfInjury], ['Trauma Severity', data.traumaSeverity],
            ['Aggravating Factors', data.aggravating], ['Relieving Factors', data.relieving],
            ['Symptom Progression', data.symptomProgression], ['Pain Irritability', data.painIrritability],
            ['Functional Impact', data.functionalImpact],
            ['Red Flags', data.redFlags?.join(', ')], ['Functional Limitations', data.functionalLimitations?.join(', ')],
            ['Pain Behavior', data.painBehavior?.join(', ')], ['Pain Distribution', data.painDistribution?.join(', ')],
            ['Inflammation Symptoms', data.inflammationSymptoms?.join(', ')], ['Instability Symptoms', data.instabilitySymptoms?.join(', ')],
            ['Morning Function', data.morningFunction?.join(', ')], ['Previous History', data.previousHistory?.join(', ')],
          ].filter(([, v]) => v && v !== '0').map(([l, v]) => (
            <div key={l} style={{ marginBottom: 4 }}><span style={{ fontWeight: 700, color: '#475569' }}>{l}: </span>{v}</div>
          ))}
        </div>
      </section>
    )}

    {(data.observations?.length > 0 || data.rom_list?.length > 0 || data.special_list?.length > 0) && (
      <section style={{ marginBottom: 14 }}>
        <h2 style={{ fontSize: 13, fontWeight: 800, background: '#ecfdf5', padding: '4px 8px', margin: '0 0 8px', borderRadius: 4 }}>🔬 Objective Examination</h2>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2,1fr)', gap: '4px 16px' }}>
          {[['Observations', data.observations?.join(', ')], ['Palpation', data.palpation?.join(', ')],
            ['Gait', data.gait?.join(', ')], ['UMN Signs', data.umnSigns?.join(', ')],
          ].filter(([, v]) => v).map(([l, v]) => (
            <div key={l} style={{ marginBottom: 4 }}><span style={{ fontWeight: 700, color: '#475569' }}>{l}: </span>{v}</div>
          ))}
        </div>
        {data.rom_list?.length > 0 && (
          <div style={{ marginTop: 8 }}>
            <strong>ROM:</strong>
            {data.rom_list.map((r, i) => <span key={i} style={{ marginLeft: 8 }}>{r.move}{r.activeRom ? ` A:${r.activeRom}°` : ''}{r.passiveRom ? ` P:${r.passiveRom}°` : ''}{r.pain ? ' ⚡' : ''}{r.endFeel ? ` [${r.endFeel}]` : ''}{!r.activeRom && !r.passiveRom && r.finding ? ` — ${r.finding}` : ''}</span>)}
          </div>
        )}
        {data.mmt_list?.length > 0 && (
          <div style={{ marginTop: 6 }}>
            <strong>MMT:</strong>
            {data.mmt_list.map((r, i) => <span key={i} style={{ marginLeft: 8 }}>{r.move} — {r.grade}</span>)}
          </div>
        )}
        {data.special_list?.length > 0 && (
          <div style={{ marginTop: 6 }}>
            <strong>Special Tests:</strong>
            {data.special_list.map((r, i) => <span key={i} style={{ marginLeft: 8 }}>{r.test} — {r.result}</span>)}
          </div>
        )}
        {data.neuro_list?.length > 0 && (
          <div style={{ marginTop: 6 }}>
            <strong>Neurology:</strong>
            {data.neuro_list.map((r, i) => <span key={i} style={{ marginLeft: 8 }}>{r.root}: S={r.sensory} M={r.motor} R={r.reflex}</span>)}
          </div>
        )}
        {data.glide_list?.length > 0 && (
          <div style={{ marginTop: 6 }}>
            <strong>Joint Glides:</strong>
            {data.glide_list.map((r, i) => <span key={i} style={{ marginLeft: 8 }}>{r.direction} — {r.finding}</span>)}
          </div>
        )}
        {data.functional_list?.length > 0 && (
          <div style={{ marginTop: 6 }}>
            <strong>Functional Tests:</strong>
            {data.functional_list.map((r, i) => <span key={i} style={{ marginLeft: 8 }}>{r.test} — {r.result}</span>)}
          </div>
        )}
      </section>
    )}

    {data.investigation_list?.length > 0 && (
      <section style={{ marginBottom: 14 }}>
        <h2 style={{ fontSize: 13, fontWeight: 800, background: '#fef9c3', padding: '4px 8px', margin: '0 0 8px', borderRadius: 4 }}>🩻 Investigations</h2>
        {data.investigation_list.map((r, i) => (
          <div key={i} style={{ marginBottom: 4 }}><span style={{ fontWeight: 700, color: '#475569' }}>{r.type}: </span>{r.finding}</div>
        ))}
      </section>
    )}

    {(data.differentialDiagnosis || data.shortTermGoals || data.longTermGoals || data.treatmentPlan) && (
      <section style={{ marginBottom: 14 }}>
        <h2 style={{ fontSize: 13, fontWeight: 800, background: '#f1f5f9', padding: '4px 8px', margin: '0 0 8px', borderRadius: 4 }}>📋 Clinical Impression</h2>
        {data.differentialDiagnosis && (
          <div style={{ marginBottom: 6 }}><strong>Differential Diagnosis:</strong> {data.differentialDiagnosis}</div>
        )}
        {(data.shortTermGoals || data.longTermGoals) && (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11, marginBottom: 6 }}>
            <tbody>
              {[['Short Term Goals', data.shortTermGoals], ['Long Term Goals', data.longTermGoals]].filter(([, v]) => v).map(([label, val]) => (
                <tr key={label}>
                  <td style={{ border: '1px solid #cbd5e1', padding: '6px 10px', fontWeight: 700, background: '#f8fafc', whiteSpace: 'nowrap', verticalAlign: 'top' }}>{label}</td>
                  <td style={{ border: '1px solid #cbd5e1', padding: '6px 10px', whiteSpace: 'pre-wrap' }}>{val}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {data.treatmentPlan && (
          <div><strong>Treatment Plan:</strong><div style={{ whiteSpace: 'pre-wrap', lineHeight: 1.6, marginTop: 4 }}>{data.treatmentPlan}</div></div>
        )}
      </section>
    )}

    {isSubmitted && diagnosisData?.primary && (
      <section style={{ marginBottom: 14 }}>
        <h2 style={{ fontSize: 13, fontWeight: 800, background: '#ecfdf5', padding: '4px 8px', margin: '0 0 8px', borderRadius: 4 }}>🧠 AI Clinical Assessment</h2>
        <div style={{ backgroundColor: '#fffbeb', border: '1px solid #fcd34d', borderRadius: 6, padding: '6px 10px', marginBottom: 8, fontSize: '0.72rem', color: '#92400e', lineHeight: 1.5 }}>
          <strong>AI-generated content — clinical review required.</strong> This assessment is produced by an AI model and may contain errors or omissions. It is intended to support, not replace, clinical judgement. Always verify findings against the patient's full clinical picture before acting.
        </div>
        <div style={{ marginBottom: 6 }}><strong>Primary Impression:</strong> {diagnosisData.primary}</div>
        {diagnosisData.differentials?.length > 0 && <div style={{ marginBottom: 6 }}><strong>Differentials:</strong> {diagnosisData.differentials.join(' · ')}</div>}
        {diagnosisData.clinicalReasoning?.length > 0 && (
          <div style={{ marginBottom: 6 }}><strong>Clinical Reasoning:</strong>{diagnosisData.clinicalReasoning.map((p, i) => <div key={i} style={{ marginLeft: 12 }}>• {p}</div>)}</div>
        )}
        {diagnosisData.physiotherapyManagementPlan?.length > 0 && (
          <div style={{ marginBottom: 6 }}><strong>Physiotherapy Plan:</strong>{diagnosisData.physiotherapyManagementPlan.map((p, i) => <div key={i} style={{ marginLeft: 12 }}>• {p}</div>)}</div>
        )}
        {diagnosisData.keyExercises?.length > 0 && (
          <div style={{ marginBottom: 6 }}><strong>Key Exercises:</strong>{diagnosisData.keyExercises.map((p, i) => <div key={i} style={{ marginLeft: 12 }}>• {p}</div>)}</div>
        )}
        {diagnosisData.contraindications?.length > 0 && (
          <div style={{ marginBottom: 6 }}><strong>Contraindications:</strong>{diagnosisData.contraindications.map((p, i) => <div key={i} style={{ marginLeft: 12 }}>• {p}</div>)}</div>
        )}
        {diagnosisData.precautions?.length > 0 && (
          <div><strong>Precautions:</strong>{diagnosisData.precautions.map((p, i) => <div key={i} style={{ marginLeft: 12 }}>• {p}</div>)}</div>
        )}
      </section>
    )}
  </>
);

export default OrthoPrintSummary;
